import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter, Button, Textarea, Checkbox, Label,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useAddCustomerNote } from '@/api/customer-basic';

/** Extract a human-readable message from an axios/unknown error. */
function errorMessage(err: unknown): string {
  const res = (err as { response?: { data?: { error_description?: string; error?: string; message?: string } } })?.response;
  return res?.data?.error_description ?? res?.data?.error ?? res?.data?.message ?? 'Something went wrong. Please try again.';
}

/**
 * Add Note dialog — posts an internal note to `POST /customers/:id/notes`
 * (auto-pinned by default). On success it closes and the mutation invalidates
 * the activity timeline. Mutation errors surface in an in-dialog Alert.
 */
export function AddNoteDialog({
  customerId, open, onOpenChange,
}: { customerId: number; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [note, setNote] = useState('');
  const [pinned, setPinned] = useState(true);
  const mutation = useAddCustomerNote(customerId);

  const close = (next: boolean) => {
    if (mutation.isPending) return;
    if (!next) {
      setNote('');
      setPinned(true);
      mutation.reset();
    }
    onOpenChange(next);
  };

  const submit = () => {
    if (!note.trim()) return;
    mutation.mutate(
      { note_description: note.trim(), is_pinned: pinned },
      { onSuccess: () => close(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Add note</DialogTitle>
          <DialogDescription>Record an internal note on this customer.</DialogDescription>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {mutation.isError ? <Alert tone="danger" title="Could not save note">{errorMessage(mutation.error)}</Alert> : null}
          <Field label="Note" required>
            {(p) => (
              <Textarea
                {...p}
                rows={5}
                placeholder="Enter note content…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <Label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer', fontSize: 'var(--vn-font-size-sm)' }}>
            <Checkbox checked={pinned} onCheckedChange={(v) => setPinned(v === true)} />
            <span>Pin this note (pinned notes appear at the top)</span>
          </Label>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" disabled={mutation.isPending} onClick={() => close(false)}>Cancel</Button>
          <Button variant="primary" loading={mutation.isPending} disabled={!note.trim()} onClick={submit}>Save note</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddNoteDialog;
